import { Skeleton } from '../ui/skeleton';
import { APP_I18N, CONTENT_LOCALE } from '@/client/confs/app';
import useLocale from '@/client/hooks/use-locale';
import { api } from '@/client/utils/api';
import { useApiTranslation } from '@/libs/react-sdk/api/use-api-translation';
import useInViewport from 'ahooks/lib/useInViewport';
import { forwardRef, HTMLAttributes, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ApiTranslationProps = PropsWithChildren & HTMLAttributes<HTMLSpanElement>;

const DISABLE_TRANSLATION = import.meta.env.VITE_DISABLE_API_TRANSLATION === 'true';

let globalTranslationReady = false;
let globalReadyTimer: ReturnType<typeof setTimeout> | undefined;
if (!globalReadyTimer) {
  globalReadyTimer = setTimeout(() => { globalTranslationReady = true; }, 2000);
}

export const ApiTranslation = forwardRef<HTMLSpanElement, ApiTranslationProps>(
  ({ children, ...attrs }: ApiTranslationProps, ref) => {
    // Skip all translation logic when disabled (e.g. content already in target locale)
    if (DISABLE_TRANSLATION) return <span ref={ref} {...attrs}>{children}</span>;

    const elementRef = useRef<HTMLSpanElement | null>(null);
    const [inViewport] = useInViewport(elementRef);
    const [ready, setReady] = useState(globalTranslationReady);
    useEffect(() => {
      if (ready) return;
      const id = setTimeout(() => setReady(true), 2000);
      return () => clearTimeout(id);
    }, [ready]);
    useEffect(() => {
      if (typeof ref === 'function') {
        ref(elementRef.current);
      } else if (ref) {
        ref.current = elementRef.current;
      }
    }, [ref]);
    const { t, i18n } = useTranslation();
    const { locale, defaultLocale } = useLocale();
    const text = typeof children === 'string' ? children.trim() : '';
    const hasTr = text.length > 0 && i18n.exists(text.toLocaleLowerCase());
    // Use CONTENT_LOCALE (actual content language) for translation direction;
    // fall back to defaultLocale for non-whitelabel deployments
    const contentLocale = CONTENT_LOCALE || defaultLocale;
    const enabled = ready && text.length > 0 && locale !== contentLocale && !!inViewport && !hasTr;
    const { data, isLoading, isError } = useApiTranslation(
      api,
      {
        text,
        sourceLang: contentLocale,
        targetLang: locale,
      },
      { enabled, queryKey: [APP_I18N.onlineVersion] }
    );
    // Show skeleton only while loading; on error fall back to original text immediately
    if (enabled && isLoading && !isError) return <Skeleton className="w-full min-w-15 h-[1em]" />;
    return (
      <span ref={elementRef} {...attrs}>
        {hasTr ? t(text.toLowerCase()) : data?.translatedText || children}
      </span>
    );
  }
);

ApiTranslation.displayName = 'ApiTranslation';
